/* eslint-disable functional/no-expression-statement */
import * as D3Brush from "d3-brush";
import * as D3Selection from "d3-selection";
import { Option, pipe } from "fp";
import React, { RefObject, ReactElement, useLayoutEffect } from "react";
import styled, { css } from "styled-components";

import * as Animation from "~/Animation";
import * as Color from "~/Color";
import * as Data from "~/Data";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import { Markers } from "~/Timeline/Marker";

import * as EventController from "./Controller";
import { defaultToFullDay } from "./Event";
import * as EventHandler from "./Event";

export type Scrolling = {
  readonly scrollContainer: RefObject<HTMLDivElement>;
  readonly scrollTarget: RefObject<HTMLDivElement>;
};

export type ZoneProp = {
  readonly defaultZone: Time.Zone;
};

export type Brush = Timeline.Scroll.BrushProps &
  Scrolling & {
    readonly events: readonly Log.Event.Event[];
    readonly timeline: Timeline.Remote;
    readonly background: Brusher.BackgroundProvider;
    readonly milestones: readonly Timeline.Milestone.Timezone[];
  };

const className = `brush-time-slider` as const;

export const BrushContainer = styled.div`
  position: relative;
  box-sizing: border-box;
  margin-left: calc(
    ${() => Timeline.Menu.width()} + ${() => Timeline.Fade.width()}
  );
  margin-right: ${() => Timeline.Fade.width()};
  padding-top: calc(
    ${() => Brusher.verticalPadding()} -
      calc(${() => Timeline.Event.Line.height()} / 2)
  );
  padding-bottom: ${() => Brusher.verticalPadding()};
  height: calc(${() => Brusher.height()} + 1.3em);
`;

export function Brusher({
  events,
  timeline,
  background,
  scrollContainer,
  scrollTarget,
  containerWidth,
  milestones,
}: Brush) {
  const resolvedTimeline = Remote.toUndefined(timeline);
  const defaultZone = Time.Zone.use();

  return pipe(
    events.length > 0 ? resolvedTimeline : undefined,
    Option.fromNullable,
    Option.map((resolvedTimeline) => (
      <BrushContainer>
        {events.map((event, index) => (
          <Brusher.Event
            key={index}
            timeline={resolvedTimeline}
            event={event}
            background={background}
          />
        ))}
        <Brush.TimeLabels
          remoteTimeline={timeline}
          containerWidth={containerWidth}
        />
        <Brush.Provider
          scrollContainer={scrollContainer}
          scrollTarget={scrollTarget}
          milestones={milestones}
          defaultZone={defaultZone}
        />
      </BrushContainer>
    )),
    Option.getOrElseW(() => Brusher.Empty())
  );
}

export namespace Brusher {
  export import Controller = EventController;
  export import BrushHandlers = EventHandler.BrushHandlers;

  export const height = () => "3em";
  export const verticalPadding = () => "1.5em" as const;

  export const Empty = (): ReactElement => <BrushContainer />;

  export const Event = (props: BrushEvent) => (
    <Container {...props}>
      <Brusher.Line background={props.background(props.event)} />
    </Container>
  );

  export type BackgroundProvider = ({ status }: Log.Event.Event) => Color.Color;

  export type BrushEvent = {
    readonly timeline: Timeline;
    readonly event: Log.Event.Event;
    readonly background: BackgroundProvider;
  };

  export const Container = styled.div<BrushEvent>`
    z-index: ${() => Timeline.Event.zIndex()};
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    ${({ event, timeline }) =>
      pipe(
        Timeline.Event.location(timeline, event),
        ({ left, right }) =>
          css`
            left: ${left * 100}%;
            right: ${right * 100}%;
          `
      )};

    height: 2em;
    cursor: pointer;
    margin-bottom: 24px;

    ${() => Animation.Transition.styles(Animation.medium())};
  `;

  export function Line(line: Timeline.Event.Line) {
    return (
      <Timeline.Event.Line.Container {...line}>
        <Timeline.Event.Line.Background {...line} />
      </Timeline.Event.Line.Container>
    );
  }
}

export namespace TimeSlider {}

export namespace Brush {
  const height = 90;
  const topMargin = 0;
  const leftMargin = 0;
  const rightMargin = 0;
  const bottomMargin = 40;

  export type BrushSelection = readonly [number, number];

  export const PaintContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    box-sizing: border-box;
    height: ${() => Brusher.height()};
    width: 100%;
    z-index: ${() => Timeline.Event.zIndex() + 1};
  `;

  type GenerateProps = ZoneProp &
    Scrolling & {
      readonly brushWidth: number;
      readonly milestones: readonly Timeline.Milestone.Timezone[];
      readonly updateBrush: (selection: BrushSelection) => void;
    };

  export const brushStartOfDayForZone = (
    brushWidth: number,
    milestones: readonly Timeline.Milestone.Timezone[],
    defaultZone: Time.Zone
  ): number => {
    const driverZone =
      milestones.find((m) => m.type === "Driver") ?? defaultZone;
    const driverZoneDelta =
      (Time.Zone.toOffset(defaultZone) - Time.Zone.toOffset(driverZone)) / 60;
    const defaultZoneDelta =
      (Time.Zone.toOffset(Time.Zone.local()) -
        Time.Zone.toOffset(defaultZone)) /
      60;
    const hoursOfDataBeforeDayStart = 12 - defaultZoneDelta;
    return (
      EventHandler.oneHour(brushWidth) *
      (hoursOfDataBeforeDayStart - driverZoneDelta)
    );
  };

  export const generate = ({
    brushWidth,
    scrollContainer,
    scrollTarget,
    milestones,
    defaultZone,
    updateBrush,
  }: GenerateProps) => {
    const brushAreaHeight = height - topMargin - bottomMargin;
    const brushAreaWidth = brushWidth - leftMargin - rightMargin;

    D3Selection.selectAll(`.${className}-svg`).remove();

    const svg = D3Selection.select(`.${className}`)
      .append("svg")
      .attr("class", `${className}-svg`)
      .attr("width", brushWidth)
      .attr("height", height)
      .attr("style", `background-color: transparent`)
      .append("g")
      .attr("transform", `translate(${leftMargin},${topMargin})`);

    const bg = svg.append("g").attr("class", `${className}-bg`);

    type Selection = {
      readonly selection: readonly [number, number];
    };
    const onBrush = (selection: Selection) => {
      if (selection.selection) {
        if (scrollContainer?.current && scrollTarget?.current) {
          const left = selection.selection[0];
          const width = selection.selection[1] - left;

          const brushToWidthRation = brushWidth / width;
          const adjustedWidth = brushToWidthRation * brushWidth;

          // eslint-disable-next-line functional/immutable-data
          scrollContainer.current.scrollLeft = left * brushToWidthRation;

          // eslint-disable-next-line functional/immutable-data
          scrollTarget.current.style.width = `${adjustedWidth}px`;

          updateBrush(selection.selection);
        }
      }
    };

    const brush = D3Brush.brushX<Timeline>()
      .handleSize(20)
      .extent([
        [0, 0],
        [brushAreaWidth, brushAreaHeight],
      ])
      .on("brush", (selection) => onBrush(selection));

    svg.append("g").attr("clip-path", "url(#clip)");

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line functional/no-expression-statement
    bg.call(brush);

    // Initially select the driver's day on the time slider to be consistent with the default
    // display of the timeline.
    const defaultSelection = defaultToFullDay(brushAreaWidth)
      ? EventHandler.defaultSelection(
          brushAreaWidth,
          brushStartOfDayForZone(brushWidth, milestones, defaultZone),
          24
        )
      : EventHandler.defaultSelection(
          brushAreaWidth,
          brushStartOfDayForZone(brushWidth, milestones, defaultZone),
          12
        );

    EventHandler.moveBrush(bg, brush, defaultSelection);
    onBrush({ selection: defaultSelection });

    return EventHandler.generate({
      brushAreaWidth,
      bg,
      brush,
      scrollContainer,
      scrollTarget,
      milestones,
      defaultZone,
    });
  };

  export type PaintProps = ZoneProp &
    Scrolling & {
      readonly milestones: readonly Timeline.Milestone.Timezone[];
    };

  const Paint = ({
    scrollContainer,
    scrollTarget,
    milestones,
    defaultZone,
  }: PaintProps) => {
    const {
      current: brushSelection,
      updateState: updateBrush,
    } = Data.SharedState.use<BrushSelection>("BrushSelection", [0, 0]);

    // eslint-disable-next-line functional/no-let
    let eventHandlers: EventHandler.BrushHandlers | undefined = undefined;

    useLayoutEffect(() => {
      if (
        scrollContainer &&
        scrollContainer.current &&
        scrollTarget &&
        scrollTarget.current
      ) {
        const containerWidth = scrollTarget.current.offsetWidth;

        if (containerWidth > 0) {
          eventHandlers = generate({
            brushWidth: containerWidth,
            scrollContainer,
            scrollTarget,
            milestones,
            defaultZone,
            updateBrush,
          });

          if (
            eventHandlers &&
            brushSelection &&
            brushSelection[0] !== 0 &&
            brushSelection[1] !== 0
          ) {
            eventHandlers.onSetBrush(brushSelection);
          }

          return EventController.bind(scrollContainer, eventHandlers);
        }
      }
    }, []);

    return <PaintContainer className={className} />;
  };

  export const Provider = React.memo<PaintProps>(
    Paint,
    (prevProps, nextProps) =>
      prevProps.scrollContainer?.current ==
        nextProps.scrollContainer?.current &&
      prevProps.scrollTarget?.current == nextProps.scrollTarget?.current
  );

  const LabelsContainer = styled.div`
    position: absolute;
    top: 70px;
    width: 100%;
    box-sizing: border-box;
    height: 1.3em;
  `;

  export type TimeLabelProps = {
    readonly remoteTimeline: Remote<Timeline>;
    readonly containerWidth: number;
  };

  export const TimeLabels = ({
    remoteTimeline,
    containerWidth,
  }: TimeLabelProps) => {
    return (
      <LabelsContainer>
        <Markers
          timeline={remoteTimeline}
          bottomLabelOnly={true}
          containerWidth={containerWidth}
        />
      </LabelsContainer>
    );
  };
}
